/* Shared helper: resolve a nature-layer key ('rain'|'waves'|'forest') to an
 * actual MusicTrack from /api/music.
 *
 * Previously each page had its own tiny implementation of this, which was
 * fragile — the token 'waves' matches the title "Deep Sleep — Pink Noise &
 * Delta Waves" before any real Ocean track, so the first copy picked the
 * wrong file. This version:
 *   1. Prefers tracks whose `category` is Nature (not Ambient, Sleep, etc.).
 *   2. Scans a stable, ordered token list per key.
 *   3. Matches titles word-boundary-ish so 'Delta Waves' no longer wins.
 */

const RULES = {
    rain: {
        // Oceans/rain titles live in the Nature category. 'rain' is a safe
        // substring; 'thunder' + 'storm' are fallbacks for lighter rain beds.
        categories: ['nature', 'rain'],
        titleIncludes: ['rain', 'storm', 'thunder'],
    },
    waves: {
        categories: ['nature', 'ocean', 'waves'],
        // Beach/ocean titles. Avoid matching "Delta Waves" by requiring one
        // of: 'ocean', 'beach', 'sea' alongside — 'waves' only is a fallback
        // after a category match.
        titleIncludes: ['ocean', 'beach', 'sea', 'rolling waves'],
        titleFallback: ['waves'],
    },
    forest: {
        categories: ['nature', 'forest'],
        titleIncludes: ['forest', 'rainforest', 'trees', 'wind'],
    },
};

function _norm(s) { return (s || '').toString().toLowerCase(); }

export function resolveNatureTrack(catalog, key) {
    if (!Array.isArray(catalog) || !key) return null;
    const rule = RULES[key];
    if (!rule) return null;

    const categoryMatches = catalog.filter((t) => {
        const c = _norm(t.category);
        return rule.categories.some((cat) => c.includes(cat));
    });

    // Pass 1: category match + strong title token.
    for (const t of categoryMatches) {
        const title = _norm(t.title);
        if (rule.titleIncludes.some((tok) => title.includes(tok))) return t;
    }

    // Pass 2: any strong title token, even if category is off.
    for (const t of catalog) {
        const title = _norm(t.title);
        if (rule.titleIncludes.some((tok) => title.includes(tok))) return t;
    }

    // Pass 3: fallback tokens within the category subset only. This is where
    // 'waves' alone can match — but constrained to tracks already in a Nature
    // category so 'Delta Waves' (Ambient/Sleep) can't win.
    if (rule.titleFallback) {
        for (const t of categoryMatches) {
            const title = _norm(t.title);
            if (rule.titleFallback.some((tok) => title.includes(tok))) return t;
        }
    }
    return null;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function resolveNatureUrl(catalog, key) {
    const t = resolveNatureTrack(catalog, key);
    if (!t?.audio_url) return null;
    const url = t.audio_url;
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${BACKEND_URL}${url}`;
}
